import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@cannect/lib/utils'
import { useRecurrencyCards } from '@cannect/services/resources/recurrency/hooks'
import type { RecurrencyCard } from '@cannect/services/resources/recurrency/types'
import { Skeleton } from '@cannect/new-components/atoms/Skeleton'
import { recurrencyCopy } from '../copy'

type RecurrencyCardPickerProps = {
  value: number | null
  onChange: (cardId: number) => void
  preselectId?: number | null
  error?: string | null
  // "Minhas assinaturas" (008) esconde o cadastro de cartão e troca o texto do não elegível.
  showAddNew?: boolean
  helpText?: string
  disabledReason?: string
}

// Aceita MM/AA, MM/AAAA ou AAAA-MM; formato desconhecido não bloqueia.
export const isCardExpired = (expirationDate: string | null, now = new Date()) => {
  if (!expirationDate) return false
  const slash = expirationDate.match(/^(\d{1,2})\/(\d{2,4})$/)
  const iso = expirationDate.match(/^(\d{4})-(\d{2})/)
  let month = 0
  let year = 0
  if (slash) {
    month = Number(slash[1])
    year = Number(slash[2].length === 2 ? `20${slash[2]}` : slash[2])
  } else if (iso) {
    month = Number(iso[2])
    year = Number(iso[1])
  }
  if (!month || !year) return false
  return year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1)
}

// Cartão sem origem de série (delta B3: `recurrencyEligible === false`) não
// serve para a cobrança recorrente sem CVV; `null` não bloqueia.
export const isCardSelectable = (card: RecurrencyCard) =>
  card.recurrencyEligible !== false && !isCardExpired(card.expirationDate)

const getDisabledReason = (card: RecurrencyCard, notEligibleText: string) => {
  if (isCardExpired(card.expirationDate)) return recurrencyCopy.cards.expired
  if (card.recurrencyEligible === false) return notEligibleText
  return null
}

export const RecurrencyCardPicker = ({
  value,
  onChange,
  preselectId,
  error,
  showAddNew = true,
  helpText,
  disabledReason = recurrencyCopy.cards.notReleased
}: RecurrencyCardPickerProps) => {
  const { data: cards = [], isLoading } = useRecurrencyCards()

  useEffect(() => {
    if (value || !cards.length) return
    const preselected = cards.find((card) => card.id === preselectId && isCardSelectable(card))
    if (preselected) onChange(preselected.id)
  }, [cards, preselectId, value])

  if (isLoading) return <Skeleton className="h-24 w-full" />

  return (
    <div className="flex flex-col gap-2">
      {cards.length ? (
        <RadioGroupPrimitive.Root
          aria-label="Cartão da assinatura"
          value={value ? String(value) : undefined}
          onValueChange={(next) => onChange(Number(next))}
          className="flex flex-col gap-2">
          {cards.map((card) => {
            const reason = getDisabledReason(card, disabledReason)
            const descriptionId = `recurrency-card-${card.id}-reason`

            return (
              <label
                key={card.id}
                htmlFor={`recurrency-card-${card.id}`}
                className={cn(
                  'flex items-center gap-3 rounded-lg border border-solid border-gray-300 bg-white p-3',
                  reason ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                )}>
                <RadioGroupPrimitive.Item
                  id={`recurrency-card-${card.id}`}
                  value={String(card.id)}
                  disabled={Boolean(reason)}
                  aria-describedby={reason ? descriptionId : undefined}
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 border-solid border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 data-[state=checked]:border-primary-700">
                  <RadioGroupPrimitive.Indicator className="h-2 w-2 rounded-full bg-primary-700" />
                </RadioGroupPrimitive.Item>
                <span className="flex flex-col">
                  <span className="text-sm font-semibold capitalize text-gray-900">
                    {card.brand || 'Cartão'} •••• {card.lastDigits}
                  </span>
                  {reason && (
                    <span id={descriptionId} className="text-xs text-gray-600">
                      {reason}
                    </span>
                  )}
                </span>
              </label>
            )
          })}
        </RadioGroupPrimitive.Root>
      ) : (
        <p className="text-sm text-gray-600">{recurrencyCopy.cards.empty}</p>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      {helpText && <p className="text-sm text-gray-600">{helpText}</p>}
      {showAddNew && (
        <Link to="/conta?pagina=cartoes" className="text-sm font-semibold text-primary-700 underline">
          {recurrencyCopy.cards.addNew}
        </Link>
      )}
    </div>
  )
}
